import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  GoQuestion,
  GoSignOut,
  GoShield,
  GoBell,
  GoGear,
} from "react-icons/go";
import Sidebar from "./Sidebar";
import { ROLES, isLawyerOnlyRoles } from "../../config/roles";
import useTranslate from "../../hooks/useTranslate";

const ProfileSidebar = ({
  isOpen,
  onClose,
  profileData,
  getProfileDisplay,
  getProfilePicAccordingToRole,
  getInitialsForRole,
  getNameByRole,
  handleLogout,
  menuItems,
  canSwitchRoles,
  handleRoleSwitch,
  currentRole,
  isAgencyOnUI,
  isRoleSwitching,
}) => {
  const { t } = useTranslate();
  const authRoles = useSelector((state) => state.auth.roles);
  const hasLawyerRole =
    Array.isArray(authRoles) && authRoles.includes(ROLES.Lawyer);
  // Lawyer-only: trim to "My Profile" (+ Lawyer-profile/Security/FAQ below);
  // agent tools (Documents manager / My Contacts) hidden. Dual-role keeps all.
  const lawyerOnly = isLawyerOnlyRoles(authRoles);
  const visibleMenuItems = lawyerOnly
    ? (menuItems || []).filter((item) => item.path === "/profile")
    : menuItems;
  const sidebarProfileImage = getProfilePicAccordingToRole
    ? getProfilePicAccordingToRole(currentRole, profileData)
    : null;
  const sidebarProfileInitials = getInitialsForRole
    ? getInitialsForRole(currentRole, profileData)
    : "";
  const sidebarProfileName = getNameByRole
    ? getNameByRole(currentRole, profileData)
    : "";
  const agencyProfileImage = getProfilePicAccordingToRole
    ? getProfilePicAccordingToRole(ROLES.AgencyAdmin, profileData)
    : null;
  const agencyProfileInitials = getInitialsForRole
    ? getInitialsForRole(ROLES.AgencyAdmin, profileData)
    : "";

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} side="right">
      <div className="p-4 border-t border border-[#0000001A]">
        {profileData && (
          <div className="flex flex-col p-4 mb-2">
            <div className="relative">
              {sidebarProfileImage ? (
                <img
                  src={sidebarProfileImage}
                  alt={sidebarProfileName || "Profile"}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-xl">
                  {sidebarProfileInitials ||
                    getProfileDisplay(profileData).initials}
                </div>
              )}
              <span className="absolute -top-1 -right-1 bg-[#E6D6FC] text-[#6E34C5] text-xs font-bold px-3 p-1 rounded-lg ">
                PRO
              </span>
            </div>
            <h3 className="font-semibold mt-3 text-lg">
              {getProfileDisplay(profileData).name}
            </h3>
            <p className="text-gray-500 text-sm">
              {getProfileDisplay(profileData).email}
            </p>
          </div>
        )}

        {/* Mobile Role Switcher - unified toggle */}
        {canSwitchRoles && (
          <div className="px-4 py-3 border-t border-[#D6DBEA]">
            <div className="flex items-center gap-2 py-2 px-3 bg-[#F8F9FB] rounded-lg justify-between">
              <div className="flex items-center gap-2 ">
                {agencyProfileImage ? (
                  <img
                    src={agencyProfileImage}
                    alt={
                      getNameByRole
                        ? getNameByRole(ROLES.AgencyAdmin, profileData) ||
                          "Agency profile"
                        : "Agency profile"
                    }
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <span className="text-xs flex justify-center items-center w-8 h-8 rounded-full bg-gray-200">
                    {agencyProfileInitials || "-"}
                  </span>
                )}
                <span className="text-black text-base font-medium">
                  Switch To Agency
                </span>
              </div>
              <div
                onClick={() => {
                  if (isRoleSwitching) return;
                  handleRoleSwitch &&
                    handleRoleSwitch(
                      isAgencyOnUI ? ROLES.Agent : ROLES.AgencyAdmin
                    );
                }}
                className={`w-12 h-7 flex items-center rounded-full cursor-pointer transition-colors duration-300 ${
                  isAgencyOnUI ? "bg-[#2283FF]" : "bg-gray-300"
                } ${isRoleSwitching ? "opacity-60 cursor-not-allowed" : ""}`}
                aria-disabled={isRoleSwitching}
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                    isAgencyOnUI ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </div>
            </div>
          </div>
        )}

        <nav className="flex flex-col space-y-1 mb-4 border-t border-[#D6DBEA] pt-2">
          {visibleMenuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className="flex items-center gap-4 px-3 py-2.5 text-base font-medium rounded-md text-[#0A0F2D] hover:bg-gray-100"
            >
              {item.icon}
              <span className="font-medium">{item.title}</span>
            </Link>
          ))}
          {hasLawyerRole && (
            <Link
              to="/settings/lawyer-profile"
              onClick={() => onClose()}
              className="flex items-center gap-4 px-3 py-2.5 text-base font-medium rounded-md text-[#0A0F2D] hover:bg-gray-100"
            >
              <GoGear size={20} />
              <span className="font-medium">{t("Lawyer profile")}</span>
            </Link>
          )}
          <Link
            to="/settings/security"
            onClick={() => onClose()}
            className="flex items-center gap-4 px-3 py-2.5 text-base font-medium rounded-md text-[#0A0F2D] hover:bg-gray-100"
          >
            <GoShield size={20} />
            <span className="font-medium">Security</span>
          </Link>
          <Link
            to="/settings/notifications"
            onClick={() => onClose()}
            className="flex items-center gap-4 px-3 py-2.5 text-base font-medium rounded-md text-[#0A0F2D] hover:bg-gray-100"
          >
            <GoBell size={20} />
            <span className="font-medium">Notifications</span>
          </Link>
          <Link
            to="/faq"
            onClick={() => onClose()}
            className="flex items-center gap-4 px-3 py-2.5 text-base font-medium rounded-md text-[#0A0F2D] hover:bg-gray-100"
          >
            <GoQuestion size={20} />
            <span className="font-medium">FAQ</span>
          </Link>
        </nav>

        <div className="px-3">
          <button
            onClick={() => {
              handleLogout();
              onClose();
            }}
            className="w-full flex items-center gap-4 px-3 py-2.5 text-base font-medium rounded-md text-red-600 border border-red-200 hover:bg-red-50"
          >
            <GoSignOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </Sidebar>
  );
};

export default ProfileSidebar;
